import { useState } from 'react';
import {
  useTranslation,
  GenericLink,
  useBreakpoint,
  Breakpoints,
  NextgenImage,
  useNextgenRouter,
  useSession,
  LoaderType,
} from '@nextgen-web-framework/all';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import { LoadingList } from '../ShopByBrands/types/propsType';
import { getGradientClass, ThemeColorType } from '../../utils/themeColor';
import { createDynamicUrl } from '../../utils/dynamicUrlGenerator';
import styles from './ImagesAndLinksContainer.module.scss';
import { ImagesAndLinksContainerProps, NavigationCategory, SubCategory } from './types/propsType';
const generateUrl = (url: string, locale?: string, aboNumber?: string, queryParams?: string[]) => {
  if (queryParams?.length) {
    return createDynamicUrl(url, queryParams, locale, aboNumber);
  }
  return url || '';
};

const ImagesAndLinksContainer = (props: ImagesAndLinksContainerProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>();
  const viewport = useBreakpoint();
  const t = useTranslation();
  const isMobile = [Breakpoints.SM, Breakpoints.XS].includes(viewport as Breakpoints);
  const [loading, setLoading] = useState<LoadingList>({});
  const router = useNextgenRouter();
  const { session } = useSession();
  const setImgLoading = (index: number) => {
    setLoading((prev) => ({
      ...prev,
      [`${index}`]: false,
    }));
  };
  const getGradientColor = (item: NavigationCategory) =>
    item.linearGradient ? getGradientClass(item.linearGradient as ThemeColorType) : `${item.overlayGradient}-gradient`;

  const prepareSubCategories = (
    subCategories: SubCategory[] | undefined = [],
    viewAllURL?: string,
    viewAllTarget?: string,
    queryParams?: string[],
  ): SubCategory[] => {
    const subCategoriesList = [...subCategories];
    if (isMobile && viewAllURL) {
      subCategoriesList.unshift({
        title: t('web.megamenu.viewAll'),
        url: viewAllURL,
        target: viewAllTarget,
        queryParams: queryParams,
      });
    }
    return subCategoriesList;
  };

  const renderSubCategories = (subCategories: SubCategory[] = []): React.ReactNode => (
    <ul className={`${styles['list']} ${styles['open']}`}>
      {subCategories.map((subCategory) => {
        const url = generateUrl(
          subCategory.url,
          router.params?.locale as string,
          session.aboNum as string,
          subCategory.queryParams,
        );
        return (
          <li className={styles['list-item']} key={subCategory.title}>
            <GenericLink
              className={styles['section-link-item-container']}
              href={url}
              forceNative={false}
              target={subCategory.target}
            >
              {subCategory.title}
            </GenericLink>
          </li>
        );
      })}
    </ul>
  );

  const handleMobileImageClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, title: string) => {
    if (selectedCategory === title) {
      setSelectedCategory('');
    } else {
      setSelectedCategory(title);
      setTimeout(() => {
        const element = event.target as HTMLDivElement;

        // Consider adding the header height to the calculation if it will be used on this page in the future.
        const headers = document.querySelectorAll(
          '#mobile-topbar-navigation-header, [class*="shop-by-page-navigator"]',
        );

        // Calculate the total height of all header elements
        const totalHeaderHeight = Array.from(headers).reduce(
          (total, header) => total + (header as HTMLElement).offsetHeight,
          0,
        );

        // Scroll the element into view, accounting for the total header and title height
        const offsetPosition = element.getBoundingClientRect().top + window.scrollY - totalHeaderHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  const getImageUrl = (item: NavigationCategory) => {
    const image = item?.imageList?.find((item) => item.viewport.includes(viewport));
    return image?.url;
  };

  const imgMenu = (item: NavigationCategory, index: number, imgLoading: boolean) => {
    const visibilityStyle: React.CSSProperties = imgLoading ? { visibility: 'hidden' } : {};

    return (
      <>
        <NextgenImage
          key={`${item.title}`}
          className="lazy-image-and-link"
          src={getImageUrl(item) ?? ''}
          alt={item.title}
          style={visibilityStyle}
          onLoad={() => setImgLoading(index)}
          loaderType={LoaderType.CONTENTSTACK}
        />
        {imgLoading && <SkeletonLoader height={120} borderRadius={12} />}
        <span style={visibilityStyle} className={`${styles['image-text']} ${styles[getGradientColor(item)]}`}>
          {item.title}
        </span>
      </>
    );
  };

  const renderDesktopCategory = (item: NavigationCategory, index: number, imgLoading: boolean) => {
    const url = generateUrl(item.link?.url || '', router.params?.locale as string, session.aboNum, item.queryParams);
    return (
      <div key={item.title} className={styles['category-container']}>
        <div className={styles['image-container']} data-testid={`images-data-test-id-${index}`}>
          {item?.link?.url ? (
            <GenericLink href={url} className={styles['category-image']} target={item.link.target}>
              {imgMenu(item, index, imgLoading)}
            </GenericLink>
          ) : (
            <div className={styles['category-image']}>{imgMenu(item, index, imgLoading)}</div>
          )}
        </div>
        {renderSubCategories(item.subCategories)}
      </div>
    );
  };

  return isMobile ? (
    // Mobile View
    <div className={styles['images-and-links-container']} data-testid="menu-images-with-links-mobile-id">
      <h4 className={styles['images-and-links-container-heading']}>{props.allTitle}</h4>
      <div className={styles['images-and-links-container-items']}>
        {props.categories.map((item: NavigationCategory, index: number) => (
          <div
            key={item.title}
            className={`${styles['images-container']}`}
            data-testid={`images-data-test-id-${index}`}
            role="presentation"
            onClick={(event) => handleMobileImageClick(event, item.title)}
          >
            <div className={styles['item-image-container']}>
              <NextgenImage
                className={styles['category-image']}
                key={`${item.title}`}
                src={getImageUrl(item) ?? ''}
                alt={item.title}
                loaderType={LoaderType.CONTENTSTACK}
              />
            </div>
            <span className={`${styles['text-container']} ${styles[getGradientColor(item)]}`}>{item.title}</span>
            {selectedCategory === item.title &&
              renderSubCategories(
                prepareSubCategories(item.subCategories, item.link?.url, item.link?.target, item.queryParams),
              )}
          </div>
        ))}
      </div>
    </div>
  ) : (
    // Desktop/Tablet View
    <div className={styles['images-with-links-container']} data-testid="menu-images-with-links-id">
      {props.categories.map((item: NavigationCategory, index: number, cat) => {
        const imgLoading = Object.keys(loading).length !== cat.length;
        return renderDesktopCategory(item, index, imgLoading);
      })}
    </div>
  );
};
export default ImagesAndLinksContainer;
